/**
 * A level meter over any audio track, on one shared AudioContext. Used for
 * his own mic (so he sees it works before he takes anyone), for her voice on
 * his side, and for her own mic on hers (she sees it is live).
 *
 * The context starts suspended until a user gesture; `resumeAudio` is called
 * from his take and her clicks, and a suspended meter reads 0.
 */
let shared: AudioContext | null = null;

export function audioContext(): AudioContext | null {
  if (typeof window === "undefined" || typeof AudioContext === "undefined") return null;
  shared ??= new AudioContext({ latencyHint: "interactive" });
  return shared;
}

export function resumeAudio(): void {
  const c = audioContext();
  if (c && c.state === "suspended") void c.resume().catch(() => undefined);
}

export interface Meter {
  /** 0..1, a speech-scaled RMS */
  level(): number;
  stop(): void;
}

export function meter(track: MediaStreamTrack): Meter {
  const ctx = audioContext();
  if (!ctx) return { level: () => 0, stop: () => undefined };
  const src = ctx.createMediaStreamSource(new MediaStream([track]));
  const an = ctx.createAnalyser();
  an.fftSize = 512;
  an.smoothingTimeConstant = 0.3;
  src.connect(an);
  const buf = new Float32Array(an.fftSize);
  return {
    level() {
      if (ctx.state !== "running" || track.readyState === "ended" || !track.enabled) return 0;
      an.getFloatTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
      const rms = Math.sqrt(s / buf.length);
      // speech sits around 0.01–0.2 RMS; a square root spreads it over the bar
      return Math.min(1, Math.sqrt(rms * 6));
    },
    stop() {
      try {
        src.disconnect();
        an.disconnect();
      } catch {
        /* already gone */
      }
    },
  };
}
